import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { letterKeys } from '../../api/letters';
import { claimAward, readAward } from '../../api/ops';
import { Button, useToast, type ButtonSize, type ButtonVariant } from '../../ui';
import { problemWords } from '../labels';

/**
 * After any claim change: the queue, Home (its queue and Waiting on you), the check and the
 * award's letters (what they let you do depends on the claim; an award comes back to Letters to be
 * claimed again while some letters follow, APP-130).
 */
export function useClaimChanged(): (awardId: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (awardId) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['queue'] }),
      queryClient.invalidateQueries({ queryKey: ['home'] }),
      queryClient.invalidateQueries({ queryKey: ['check', awardId] }),
      queryClient.invalidateQueries({ queryKey: letterKeys.one(awardId) }),
    ]);
  };
}

/**
 * Claim an award and open its check. The claim sends the award's version (from the queue row, or
 * read first when the caller hasn't got it), so a teammate's claim in between answers "already on
 * it" instead of a silent overwrite.
 */
export function ClaimButton({
  awardId,
  displayId,
  label = 'Claim',
  variant = 'navy',
  size = 'sm',
  version,
  to,
}: {
  awardId: string;
  displayId: string;
  label?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** The version when the caller already has it (the check page). */
  version?: number;
  /** Where to go once claimed: the award's check, unless the claim starts elsewhere (letters). */
  to?: string;
}): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  const changed = useClaimChanged();
  const claim = useMutation({
    mutationFn: async () => {
      const current = version ?? (await readAward(awardId)).version;
      return claimAward(awardId, current);
    },
    onSuccess: async () => {
      await changed(awardId);
      toast.show({
        tone: 'done',
        title: `You’re on ${displayId}`,
        body: 'Your claim shows to the whole team.',
      });
      void navigate(to ?? `/queue/${awardId}`);
    },
    onError: async (error) => {
      await changed(awardId);
      toast.show({
        tone: 'problem',
        title: `Couldn’t claim ${displayId}`,
        body: problemWords(error),
      });
    },
  });
  return (
    <Button
      variant={variant}
      size={size}
      isPending={claim.isPending}
      aria-label={`${label} ${displayId}`}
      onPress={() => {
        claim.mutate();
      }}
    >
      {label}
    </Button>
  );
}
